import React, { useState } from 'react';
import {
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  Button,
  Input,
  Field,
  Select,
  Spinner,
  Text,
} from '@fluentui/react-components';
import { useAuth } from '@/auth/AuthContext';
import axios from 'axios';

const API_BASE = import.meta.env.VITE_API_BASE || '/api';

interface InviteAdminModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function InviteAdminModal({ onClose }: InviteAdminModalProps) {
  const { token } = useAuth();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'admin' | 'viewer'>('admin');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setLoading(true);

    try {
      await axios.post(`${API_BASE}/portal/invite`, { email, role }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccess(true);
      setEmail('');
      setRole('admin');
      setTimeout(onClose, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to invite admin');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DialogSurface>
      <DialogTitle>Invite Admin User</DialogTitle>
      <DialogBody>
        {success ? (
          <div className="success-message">
            <Text>✓ Invitation sent successfully!</Text>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <Field
              label="Email Address"
              required
              validationMessage={error ? error : undefined}
              validationState={error ? 'error' : 'none'}
            >
              <Input
                type="email"
                placeholder="admin@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                required
              />
            </Field>

            <Field label="Role">
              <Select value={role} onChange={(e) => setRole(e.target.value as 'admin' | 'viewer')}>
                <option value="admin">Admin - Full access to manage tenants</option>
                <option value="viewer">Viewer - Read-only access to analytics</option>
              </Select>
            </Field>

            <DialogActions>
              <Button onClick={onClose} disabled={loading}>
                Cancel
              </Button>
              <Button appearance="primary" type="submit" disabled={loading || !email}>
                {loading ? <Spinner size="small" /> : 'Send Invitation'}
              </Button>
            </DialogActions>
          </form>
        )}
      </DialogBody>
    </DialogSurface>
  );
}
